import { z } from 'zod';
import { APPOINTMENT_STATUSES, LEAD_STAGES, MEMBER_STATUSES, MEMBERSHIP_STATUSES, PAYMENT_METHODS, PAYMENT_RECORD_PROVIDERS, PAYMENT_STATUSES, REFUND_STATUSES, REPORT_VISIBILITIES } from '@bulk/types';
import { ANALYTICS_COMPARISONS, rollupUseSchema } from './backoffice';
import { dateOnlySchema, idSchema, patchOf } from './common';

export const REPORT_DATASETS = ['payments', 'refunds', 'memberships', 'members', 'visits', 'class_attendance', 'leads', 'appointments', 'sales', 'expenses', 'people'] as const;
export type ReportDataset = (typeof REPORT_DATASETS)[number];

/** Datasets that carry money: running them needs `finance.read`; the others need only `reports.read`. */
export const REPORT_FINANCE_DATASETS: readonly ReportDataset[] = ['payments', 'refunds', 'memberships', 'sales'];
export const reportNeedsFinance = (dataset: ReportDataset): boolean => REPORT_FINANCE_DATASETS.includes(dataset);

export const REPORT_RANGE_PRESETS = ['today', 'this_week', 'last_week', 'this_month', 'last_month', 'this_quarter', 'last_quarter', 'this_year', 'last_year', 'custom'] as const;
export type ReportRangePreset = (typeof REPORT_RANGE_PRESETS)[number];

export const REPORT_TIME_GROUPINGS = ['day', 'week', 'month', 'quarter'] as const;
export type ReportTimeGrouping = (typeof REPORT_TIME_GROUPINGS)[number];

export const REPORT_GROUPINGS = [...REPORT_TIME_GROUPINGS, 'weekday', 'plan', 'method', 'provider', 'staff', 'member', 'class', 'trainer', 'source', 'tag', 'category', 'branch', 'client', 'department', 'role', 'leave_type', 'appointment_type', 'status'] as const;
export type ReportGrouping = (typeof REPORT_GROUPINGS)[number];

export const REPORT_PEOPLE_MEASURES = ['headcount', 'joiners', 'leavers', 'turnover', 'leave_days', 'sick_rate', 'late', 'no_shows', 'overtime'] as const;
export type ReportPeopleMeasure = (typeof REPORT_PEOPLE_MEASURES)[number];

export const REPORT_APPOINTMENT_MEASURES = ['completed', 'no_show', 'cancelled', 'show_rate'] as const;
export type ReportAppointmentMeasure = (typeof REPORT_APPOINTMENT_MEASURES)[number];

export const REPORT_MEASURES = ['amount', 'count', 'average', 'won', 'lost', 'conversion', ...REPORT_APPOINTMENT_MEASURES, ...REPORT_PEOPLE_MEASURES] as const;
export type ReportMeasure = (typeof REPORT_MEASURES)[number];

const isPeopleMeasure = (measure: ReportMeasure): measure is ReportPeopleMeasure => (REPORT_PEOPLE_MEASURES as readonly string[]).includes(measure);

/** What a measure counts in: money, things, a share from 0 to 1 (`conversion`, `show_rate`, `turnover`, `sick_rate`), or a number with decimals (overtime hours). */
export type ReportMeasureKind = 'money' | 'count' | 'rate' | 'decimal';

export function reportMeasureKind(measure: ReportMeasure): ReportMeasureKind {
  if (measure === 'amount' || measure === 'average') return 'money';
  if (measure === 'overtime') return 'decimal';
  return measure === 'conversion' || measure === 'show_rate' || measure === 'turnover' || measure === 'sick_rate' ? 'rate' : 'count';
}

/** Shares and averages do not add up across rows, so part-to-whole charts refuse them. */
export const reportMeasureAdds = (measure: ReportMeasure): boolean => !['average', 'conversion', 'show_rate', 'turnover', 'sick_rate'].includes(measure);

export const REPORT_FILTERS = ['status', 'method', 'provider', 'plan', 'staff', 'amount'] as const;
export type ReportFilter = (typeof REPORT_FILTERS)[number];

export const REPORT_STAFF_NONE = 'none';

export interface ReportDatasetRules {
  groupings: readonly ReportGrouping[];
  measures: readonly ReportMeasure[];
  filters: readonly ReportFilter[];
  statuses: readonly string[];
}

const MONEY_GROUPINGS = [...REPORT_TIME_GROUPINGS, 'plan', 'method', 'provider', 'staff', 'member'] as const;
const MONEY_MEASURES = ['amount', 'count', 'average'] as const;

export const REPORT_DATASET_RULES: Record<ReportDataset, ReportDatasetRules> = {
  payments: { groupings: [...MONEY_GROUPINGS, 'client'], measures: MONEY_MEASURES, filters: REPORT_FILTERS, statuses: PAYMENT_STATUSES },
  refunds: { groupings: [...MONEY_GROUPINGS, 'client'], measures: MONEY_MEASURES, filters: REPORT_FILTERS, statuses: REFUND_STATUSES },
  memberships: { groupings: [...REPORT_TIME_GROUPINGS, 'plan', 'member'], measures: MONEY_MEASURES, filters: ['status', 'plan', 'amount'], statuses: MEMBERSHIP_STATUSES.filter((s) => s !== 'PENDING') },
  members: { groupings: [...REPORT_TIME_GROUPINGS, 'tag'], measures: ['count'], filters: ['status'], statuses: MEMBER_STATUSES },
  visits: { groupings: [...REPORT_TIME_GROUPINGS, 'weekday', 'member'], measures: ['count'], filters: [], statuses: [] },
  class_attendance: { groupings: [...REPORT_TIME_GROUPINGS, 'class', 'trainer', 'member'], measures: ['count'], filters: [], statuses: [] },
  leads: { groupings: [...REPORT_TIME_GROUPINGS, 'source', 'staff'], measures: ['count', 'won', 'lost', 'conversion'], filters: ['status', 'staff'], statuses: LEAD_STAGES },
  appointments: { groupings: [...REPORT_TIME_GROUPINGS, 'trainer', 'appointment_type', 'status', 'branch'], measures: ['count', ...REPORT_APPOINTMENT_MEASURES], filters: ['status'], statuses: APPOINTMENT_STATUSES },
  sales: { groupings: [...REPORT_TIME_GROUPINGS, 'plan', 'staff', 'branch', 'client'], measures: MONEY_MEASURES, filters: ['plan', 'staff'], statuses: [] },
  expenses: { groupings: [...REPORT_TIME_GROUPINGS, 'category', 'branch', 'method'], measures: MONEY_MEASURES, filters: ['amount'], statuses: [] },
  people: { groupings: [...REPORT_TIME_GROUPINGS, 'branch', 'department', 'role', 'leave_type'], measures: REPORT_PEOPLE_MEASURES, filters: [], statuses: [] },
};

const PEOPLE_MEASURE_LABELS: Record<ReportPeopleMeasure, string> = {
  headcount: 'Headcount',
  joiners: 'Joiners',
  leavers: 'Leavers',
  turnover: 'Turnover',
  leave_days: 'Leave days',
  sick_rate: 'Sick-leave rate',
  late: 'Late arrivals',
  no_shows: 'No-shows',
  overtime: 'Overtime hours',
};

const OUTCOME_LABELS = { won: 'Won', lost: 'Lost', conversion: 'Conversion rate', completed: 'Completed', no_show: 'No-shows', cancelled: 'Cancelled', show_rate: 'Show rate', ...PEOPLE_MEASURE_LABELS } as const;

export const REPORT_MEASURE_LABELS: Record<ReportDataset, Record<ReportMeasure, string>> = {
  payments: { amount: 'Amount', count: 'Payments', average: 'Average payment', ...OUTCOME_LABELS },
  refunds: { amount: 'Refunded', count: 'Refunds', average: 'Average refund', ...OUTCOME_LABELS },
  memberships: { amount: 'Plan value', count: 'Memberships', average: 'Average plan price', ...OUTCOME_LABELS },
  members: { amount: 'Amount', count: 'New members', average: 'Average', ...OUTCOME_LABELS },
  visits: { amount: 'Amount', count: 'Visits', average: 'Average', ...OUTCOME_LABELS },
  class_attendance: { amount: 'Amount', count: 'Attendances', average: 'Average', ...OUTCOME_LABELS },
  leads: { amount: 'Amount', count: 'Leads', average: 'Average', ...OUTCOME_LABELS },
  appointments: { amount: 'Amount', count: 'Sessions', average: 'Average', ...OUTCOME_LABELS },
  sales: { amount: 'Revenue', count: 'Sales', average: 'Average sale', ...OUTCOME_LABELS },
  expenses: { amount: 'Spent', count: 'Expenses', average: 'Average expense', ...OUTCOME_LABELS },
  people: { amount: 'Amount', count: 'People', average: 'Average', ...OUTCOME_LABELS },
};

const DATASET_NAMES: Record<ReportDataset, string> = { payments: 'payments', refunds: 'refunds', memberships: 'memberships', members: 'new members', visits: 'visits', class_attendance: 'class attendance', leads: 'leads', appointments: 'sessions', sales: 'sales', expenses: 'expenses', people: 'people' };

export function isTimeGrouping(grouping: ReportGrouping): grouping is ReportTimeGrouping {
  return (REPORT_TIME_GROUPINGS as readonly string[]).includes(grouping);
}

export const reportRangeSchema = z.object({
  preset: z.enum(REPORT_RANGE_PRESETS),
  from: dateOnlySchema.optional(),
  to: dateOnlySchema.optional(),
});
export type ReportRange = z.infer<typeof reportRangeSchema>;

export const reportFiltersSchema = z.object({
  statuses: z.array(z.string().min(1).max(40)).max(10).optional(),
  methods: z.array(z.enum(PAYMENT_METHODS)).max(PAYMENT_METHODS.length).optional(),
  providers: z.array(z.enum(PAYMENT_RECORD_PROVIDERS)).max(PAYMENT_RECORD_PROVIDERS.length).optional(),
  planIds: z.array(idSchema).max(100).optional(),
  staffUserIds: z.array(z.union([idSchema, z.literal(REPORT_STAFF_NONE)])).max(100).optional(),
  amountMinMinor: z.number().int().nonnegative().optional(),
  amountMaxMinor: z.number().int().nonnegative().optional(),
});
export type ReportFilters = z.infer<typeof reportFiltersSchema>;

const reportDefinitionShape = z.object({
  dataset: z.enum(REPORT_DATASETS),
  range: reportRangeSchema,
  groupBy: z.array(z.enum(REPORT_GROUPINGS)).min(1).max(2),
  measures: z.array(z.enum(REPORT_MEASURES)).min(1).max(REPORT_MEASURES.length),
  filters: reportFiltersSchema.default({}),
  compare: z.enum(ANALYTICS_COMPARISONS).default('none'),
});

export interface ReportProblem {
  path: Array<string | number>;
  message: string;
}

const filled = (list: readonly unknown[] | undefined) => (list?.length ?? 0) > 0;

export function reportDefinitionProblems(definition: z.output<typeof reportDefinitionShape>): ReportProblem[] {
  const problems: ReportProblem[] = [];
  const { dataset, range, groupBy, measures, filters } = definition;
  const rules = REPORT_DATASET_RULES[dataset];
  const name = DATASET_NAMES[dataset];

  if (range.preset === 'custom' && (!range.from || !range.to)) problems.push({ path: ['range'], message: 'A custom range needs both a from and a to date' });
  if (range.preset !== 'custom' && (range.from || range.to)) problems.push({ path: ['range'], message: 'Dates are only used with the custom range; choose preset "custom" or leave the dates out' });

  if (new Set(groupBy).size !== groupBy.length) problems.push({ path: ['groupBy'], message: 'Each grouping can be used once' });
  if (groupBy.filter(isTimeGrouping).length > 1) problems.push({ path: ['groupBy'], message: 'Group by at most one of day, week, month and quarter' });
  groupBy.forEach((grouping, i) => {
    if (!rules.groupings.includes(grouping)) problems.push({ path: ['groupBy', i], message: `Grouping by ${grouping} is not available for ${name}` });
  });

  if (new Set(measures).size !== measures.length) problems.push({ path: ['measures'], message: 'Each measure can be used once' });
  measures.forEach((measure, i) => {
    if (!rules.measures.includes(measure)) problems.push({ path: ['measures', i], message: `The ${measure} measure is not available for ${name}: count them instead` });
  });

  const used: Array<[ReportFilter, boolean]> = [
    ['status', filled(filters.statuses)],
    ['method', filled(filters.methods)],
    ['provider', filled(filters.providers)],
    ['plan', filled(filters.planIds)],
    ['staff', filled(filters.staffUserIds)],
    ['amount', filters.amountMinMinor !== undefined || filters.amountMaxMinor !== undefined],
  ];
  for (const [filter, isUsed] of used) {
    if (isUsed && !rules.filters.includes(filter)) problems.push({ path: ['filters'], message: `Filtering by ${filter} is not available for ${name}` });
  }
  if (rules.filters.includes('status')) {
    for (const status of filters.statuses ?? []) {
      if (!rules.statuses.includes(status)) problems.push({ path: ['filters', 'statuses'], message: `${status} is not a status of ${name}; use one of ${rules.statuses.join(', ')}` });
    }
  }
  if (dataset === 'people' && groupBy.includes('leave_type') && measures.some((measure) => measure !== 'leave_days')) {
    problems.push({ path: ['measures'], message: 'Grouped by leave type, only leave days can be counted' });
  }
  if (filters.amountMinMinor !== undefined && filters.amountMaxMinor !== undefined && filters.amountMinMinor > filters.amountMaxMinor) {
    problems.push({ path: ['filters', 'amountMinMinor'], message: 'The minimum amount is above the maximum amount' });
  }
  return problems;
}

export const reportDefinitionSchema = reportDefinitionShape.superRefine((definition, ctx) => {
  for (const problem of reportDefinitionProblems(definition)) ctx.addIssue({ code: 'custom', path: problem.path, message: problem.message });
});
export type ReportDefinition = z.infer<typeof reportDefinitionSchema>;
export type ReportDefinitionInput = z.input<typeof reportDefinitionSchema>;

export interface ReportPreset {
  id: string;
  name: string;
  definition: ReportDefinition;
}

export const REPORT_PRESETS: readonly ReportPreset[] = [
  { id: 'revenue-by-plan', name: 'Revenue by plan per month', definition: { dataset: 'payments', range: { preset: 'this_year' }, groupBy: ['month', 'plan'], measures: ['amount', 'count'], filters: {}, compare: 'none' } },
  { id: 'refunds-by-staff', name: 'Refunds by staff', definition: { dataset: 'refunds', range: { preset: 'this_month' }, groupBy: ['staff'], measures: ['amount', 'count'], filters: {}, compare: 'previous_period' } },
  { id: 'new-members-weekly', name: 'New members per week', definition: { dataset: 'members', range: { preset: 'this_quarter' }, groupBy: ['week'], measures: ['count'], filters: {}, compare: 'previous_period' } },
  { id: 'visits-by-weekday', name: 'Visits per weekday', definition: { dataset: 'visits', range: { preset: 'last_month' }, groupBy: ['weekday'], measures: ['count'], filters: {}, compare: 'none' } },
  { id: 'sales-by-staff', name: 'Sales by staff member', definition: { dataset: 'sales', range: { preset: 'this_month' }, groupBy: ['staff'], measures: ['amount', 'count'], filters: {}, compare: 'previous_period' } },
  { id: 'expenses-by-category', name: 'Expenses by category per month', definition: { dataset: 'expenses', range: { preset: 'this_year' }, groupBy: ['month', 'category'], measures: ['amount', 'count'], filters: {}, compare: 'none' } },
  { id: 'leads-by-source', name: 'Lead conversion by source', definition: { dataset: 'leads', range: { preset: 'this_year' }, groupBy: ['source'], measures: ['count', 'won', 'lost', 'conversion'], filters: {}, compare: 'none' } },
  { id: 'sessions-by-trainer', name: 'Sessions by trainer', definition: { dataset: 'appointments', range: { preset: 'this_month' }, groupBy: ['trainer'], measures: ['count', 'completed', 'no_show', 'cancelled', 'show_rate'], filters: {}, compare: 'none' } },
];

export const reportExportSchema = z.object({
  definition: reportDefinitionSchema,
  name: z.string().trim().min(1).max(120).optional(),
});
export type ReportExportInput = z.infer<typeof reportExportSchema>;

export const reportValuesSchema = z.object({
  amountMinor: z.number().int().nullable(),
  count: z.number().int(),
  averageMinor: z.number().int().nullable(),
  won: z.number().int().nullable().optional().describe('Leads only: how many of the counted leads became members'),
  lost: z.number().int().nullable().optional().describe('Leads only: how many of the counted leads were lost'),
  completed: z.number().int().nullable().optional().describe('Sessions only: how many of the counted sessions were held'),
  noShow: z.number().int().nullable().optional().describe('Sessions only: how many of the counted sessions the guest did not come to'),
  cancelled: z.number().int().nullable().optional().describe('Sessions only: how many of the counted sessions were cancelled'),
  metrics: z.record(z.string(), z.number().nullable()).optional().describe('People only: each people measure by name'),
});
export type ReportValuesDto = z.infer<typeof reportValuesSchema>;

export const reportKeySchema = z.object({
  value: z.string().nullable(),
  label: z.string(),
  labels: z.record(z.string(), z.string()).optional().describe("The gym's own names in each language, for a grouping by expense category, appointment type, department or leave type"),
  from: z.string().optional(),
  to: z.string().optional(),
});
export type ReportKeyDto = z.infer<typeof reportKeySchema>;

export const reportRowSchema = z.object({
  keys: z.array(reportKeySchema),
  other: z.boolean(),
  groups: z.number().int(),
  values: reportValuesSchema,
  previous: reportValuesSchema.nullable(),
});
export type ReportRowDto = z.infer<typeof reportRowSchema>;

const reportPeriodSchema = z.object({ from: z.string(), to: z.string() });

export const reportResultSchema = z.object({
  definition: reportDefinitionSchema,
  currency: z.string(),
  timezone: z.string(),
  generatedAt: z.string(),
  period: reportPeriodSchema,
  comparison: reportPeriodSchema.nullable(),
  rollup: rollupUseSchema.nullable(),
  /** The branches counted, for someone who works only at some; null when the report covers the whole gym. */
  branches: z.array(z.string()).nullable(),
  columns: z.object({
    groups: z.array(z.object({ grouping: z.enum(REPORT_GROUPINGS), label: z.string() })),
    measures: z.array(z.object({ measure: z.enum(REPORT_MEASURES), label: z.string(), kind: z.enum(['money', 'count', 'rate', 'decimal']) })),
  }),
  rows: z.array(reportRowSchema),
  totals: z.object({ values: reportValuesSchema, previous: reportValuesSchema.nullable() }),
  rowLimit: z.number().int(),
  truncated: z.boolean(),
  series: z.array(reportRowSchema),
});
export type ReportResultDto = z.infer<typeof reportResultSchema>;

export interface ReportGroupColumn {
  type: 'group';
  key: string;
  level: number;
  grouping: ReportGrouping;
  label: string;
}

export interface ReportMeasureColumn {
  type: 'measure';
  key: string;
  role: 'value' | 'previous' | 'change';
  measure: ReportMeasure;
  kind: ReportMeasureKind | 'percent';
  label: string;
  short: string;
}

export type ReportTableColumn = ReportGroupColumn | ReportMeasureColumn;

export function reportTableColumns(columns: ReportResultDto['columns'], withComparison: boolean): ReportTableColumn[] {
  const groups = columns.groups.map((group, level): ReportGroupColumn => ({ type: 'group', key: `group-${level}`, level, grouping: group.grouping, label: group.label }));
  const measures = columns.measures.flatMap((m): ReportMeasureColumn[] => {
    const value: ReportMeasureColumn = { type: 'measure', key: m.measure, role: 'value', measure: m.measure, kind: m.kind, label: m.label, short: m.label };
    if (!withComparison) return [value];
    return [value, { ...value, key: `${m.measure}-previous`, role: 'previous', label: `${m.label} previous`, short: 'Previous' }, { ...value, key: `${m.measure}-change`, role: 'change', kind: 'percent', label: `${m.label} change`, short: 'Change' }];
  });
  return [...groups, ...measures];
}

export function reportMeasureValue(values: ReportValuesDto | null, measure: ReportMeasure): number | null {
  if (!values) return null;
  if (isPeopleMeasure(measure)) return values.metrics?.[measure] ?? null;
  switch (measure) {
    case 'count':
      return values.count;
    case 'amount':
      return values.amountMinor;
    case 'average':
      return values.averageMinor;
    case 'won':
      return values.won ?? null;
    case 'lost':
      return values.lost ?? null;
    case 'conversion':
      return values.won === null || values.won === undefined || values.count === 0 ? null : values.won / values.count;
    case 'completed':
      return values.completed ?? null;
    case 'no_show':
      return values.noShow ?? null;
    case 'cancelled':
      return values.cancelled ?? null;
    case 'show_rate': {
      const answered = (values.completed ?? 0) + (values.noShow ?? 0);
      return values.completed === null || values.completed === undefined || answered === 0 ? null : values.completed / answered;
    }
  }
}

export function reportChange(value: number | null, previous: number | null): number | null {
  if (value === null || previous === null || previous === 0) return null;
  return Math.round(((value - previous) / Math.abs(previous)) * 1000) / 10;
}

export function reportCellValue(column: ReportMeasureColumn, values: ReportValuesDto, previous: ReportValuesDto | null): number | null {
  if (column.role === 'value') return reportMeasureValue(values, column.measure);
  if (column.role === 'previous') return reportMeasureValue(previous, column.measure);
  return reportChange(reportMeasureValue(values, column.measure), reportMeasureValue(previous, column.measure));
}

export const reportOptionsSchema = z.object({
  plans: z.array(z.object({ id: idSchema, name: z.string(), archived: z.boolean() })),
  staff: z.array(z.object({ id: idSchema, name: z.string() })),
});
export type ReportOptionsDto = z.infer<typeof reportOptionsSchema>;

export const savedReportInputSchema = z.object({
  name: z.string().trim().min(1).max(120),
  visibility: z.enum(REPORT_VISIBILITIES).default('PRIVATE'),
  definition: reportDefinitionSchema,
});
export type SavedReportInput = z.infer<typeof savedReportInputSchema>;

export const updateSavedReportSchema = patchOf(savedReportInputSchema);
export type UpdateSavedReportInput = z.infer<typeof updateSavedReportSchema>;

export const savedReportSchema = z.object({
  id: idSchema,
  name: z.string(),
  visibility: z.enum(REPORT_VISIBILITIES),
  definition: reportDefinitionSchema.nullable(),
  invalid: z.boolean(),
  problems: z.array(z.string()),
  createdByUserId: idSchema.nullable(),
  createdByName: z.string().nullable(),
  canEdit: z.boolean(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type SavedReportDto = z.infer<typeof savedReportSchema>;
